/**
 * Account Stats Store
 *
 * Zustand store for account statistics from the account-stats microservice.
 * Tracks prop firm accounts, drawdown, P&L, and trading days.
 */

import { create } from 'zustand';
import { wsService } from '../services/WebSocketService';
import { addToast } from './toastStore';

// Types
export type AccountType = 'eval' | 'funded' | 'personal';

export interface DailyPnLEntry {
  date: string;
  pnl: number;
  balance: number;
  openPnL: number;
  realizedPnL: number;
  trades?: number;
}

export interface AccountStatistics {
  accountId: number;
  brokerId: string;
  accountName: string;
  nickname: string;
  propFirm: string | null;
  propFirmId: string | null;
  accountType: AccountType;
  customFirmName?: string;
  customAccountType?: AccountType;
  daysTradedTotal: number;
  daysTradedThisWeek: number;
  daysTradedThisMonth: number;
  tradingDays: string[];
  startingBalance: number;
  currentBalance: number;
  highWaterMark: number;
  totalPnL: number;
  openPnL: number;
  realizedPnL: number;
  currentDrawdown: number;
  currentDrawdownPercent: number;
  maxDrawdownReached: number;
  maxDrawdownPercent: number;
  dailyPnL: DailyPnLEntry[];
  drawdownLimit: number | null;
  profitTargetEval: number | null;
  profitTargetPayout: number | null;
  isActive: boolean;
  lastTradeDate: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface PropFirmInfo {
  id: string;
  name: string;
  typicalDrawdownLimit?: number;
  typicalProfitTarget?: number;
}

export interface AccountStatsSummary {
  totalAccounts: number;
  evalAccounts: number;
  fundedAccounts: number;
  personalAccounts: number;
  totalBalance: number;
  totalPnL: number;
  avgDrawdownPercent: number;
}

export interface AccountConfigUpdate {
  drawdownLimit?: number | null;
  profitTargetEval?: number | null;
  profitTargetPayout?: number | null;
  customFirmName?: string;
  customAccountType?: AccountType;
  startingBalance?: number;
}

interface AccountStatsState {
  // Data
  accounts: Map<string, AccountStatistics>;
  firms: PropFirmInfo[];
  summary: AccountStatsSummary | null;

  // UI State
  isLoading: boolean;
  isModalOpen: boolean;
  selectedAccountKey: string | null; // brokerId:accountId

  // Actions
  setAccounts: (accounts: AccountStatistics[]) => void;
  updateAccount: (account: AccountStatistics) => void;
  setFirms: (firms: PropFirmInfo[]) => void;
  setSummary: (summary: AccountStatsSummary) => void;
  setLoading: (loading: boolean) => void;
  setModalOpen: (open: boolean) => void;
  setSelectedAccount: (brokerId: string, accountId: number) => void;
  clearSelectedAccount: () => void;

  // Selectors
  getAccount: (brokerId: string, accountId: number) => AccountStatistics | undefined;
  getAccountsByType: (type: AccountType) => AccountStatistics[];
  getAccountsByFirm: (firmId: string) => AccountStatistics[];
  getAllAccounts: () => AccountStatistics[];
  getActiveAccounts: () => AccountStatistics[];

  // API Actions
  requestAccounts: () => void;
  updateConfig: (brokerId: string, accountId: number, config: AccountConfigUpdate) => void;
}

export const useAccountStatsStore = create<AccountStatsState>((set, get) => ({
  // Initial state
  accounts: new Map(),
  firms: [],
  summary: null,
  isLoading: false,
  isModalOpen: false,
  selectedAccountKey: null,

  // Setters
  setAccounts: (accounts) => {
    const map = new Map<string, AccountStatistics>();
    // Guard against non-array data from backend
    if (Array.isArray(accounts)) {
      for (const account of accounts) {
        map.set(`${account.brokerId}:${account.accountId}`, account);
      }
    }
    set({ accounts: map, isLoading: false });
  },

  updateAccount: (account) => {
    set((state) => {
      const newAccounts = new Map(state.accounts);
      newAccounts.set(`${account.brokerId}:${account.accountId}`, account);
      return { accounts: newAccounts };
    });
  },

  setFirms: (firms) => set({ firms }),

  setSummary: (summary) => set({ summary }),

  setLoading: (loading) => set({ isLoading: loading }),

  setModalOpen: (open) => set({ isModalOpen: open }),

  setSelectedAccount: (brokerId, accountId) => {
    set({ selectedAccountKey: `${brokerId}:${accountId}` });
  },

  clearSelectedAccount: () => set({ selectedAccountKey: null }),

  // Selectors
  getAccount: (brokerId, accountId) => {
    return get().accounts.get(`${brokerId}:${accountId}`);
  },

  getAccountsByType: (type) => {
    return Array.from(get().accounts.values()).filter(
      (a) => (a.customAccountType || a.accountType) === type
    );
  },

  getAccountsByFirm: (firmId) => {
    return Array.from(get().accounts.values()).filter(
      (a) => a.propFirmId === firmId
    );
  },

  getAllAccounts: () => {
    return Array.from(get().accounts.values());
  },

  getActiveAccounts: () => {
    return Array.from(get().accounts.values()).filter((a) => a.isActive);
  },

  // API Actions
  requestAccounts: () => {
    set({ isLoading: true });
    wsService.emit('accountStats:get-accounts');
  },

  updateConfig: (brokerId, accountId, config) => {
    wsService.emit('accountStats:update-config', { brokerId, accountId, config });
  },
}));

// Initialize socket handlers
let handlersInitialized = false;

export function initAccountStatsHandlers(): void {
  const store = useAccountStatsStore.getState();

  // Always request fresh data on connect/reconnect
  setTimeout(() => {
    store.requestAccounts();
    console.log('[ACCOUNT-STATS-STORE] Requested fresh accounts on connect');
  }, 100);

  if (handlersInitialized) {
    console.log('[ACCOUNT-STATS-STORE] Socket handlers already initialized, fetched fresh data');
    return;
  }
  handlersInitialized = true;

  // Accounts list
  wsService.on('accountStats:accounts', (accounts: AccountStatistics[]) => {
    store.setAccounts(accounts);
  });

  // Summary
  wsService.on('accountStats:summary', (summary: AccountStatsSummary) => {
    store.setSummary(summary);
  });

  // Firms list
  wsService.on('accountStats:firms', (firms: PropFirmInfo[]) => {
    store.setFirms(firms);
  });

  // Account updates
  wsService.on('accountStats:account:updated', (stats: AccountStatistics) => {
    store.updateAccount(stats);
  });

  wsService.on('accountStats:account:stats:updated', (stats: AccountStatistics) => {
    store.updateAccount(stats);
  });

  wsService.on('accountStats:account:config:updated', (stats: AccountStatistics) => {
    store.updateAccount(stats);
    addToast('success', `Updated config for ${stats.nickname || stats.accountName}`);
  });

  // Trading day recorded
  wsService.on('accountStats:account:trading-day', (data: { stats: AccountStatistics; date: string }) => {
    store.updateAccount(data.stats);
    addToast('info', `Trading day recorded for ${data.stats.nickname}: Day ${data.stats.daysTradedTotal}`);
  });

  console.log('[ACCOUNT-STATS-STORE] Socket handlers initialized');
}

export function resetAccountStatsSocketHandlers() {
  handlersInitialized = false;
  console.log('[ACCOUNT-STATS-STORE] Reset socket handlers flag');
}

// Selector hooks for convenience
export function useAccountStats() {
  return useAccountStatsStore((state) => state.getAllAccounts());
}

export function useActiveAccountStats() {
  return useAccountStatsStore((state) => state.getActiveAccounts());
}

export function useAccountStatsSummary() {
  return useAccountStatsStore((state) => state.summary);
}

export function usePropFirms() {
  return useAccountStatsStore((state) => state.firms);
}

export function useAccountStatsModalOpen() {
  return useAccountStatsStore((state) => state.isModalOpen);
}

export function useSelectedAccountStats() {
  return useAccountStatsStore((state) => {
    if (!state.selectedAccountKey) return null;
    return state.accounts.get(state.selectedAccountKey) || null;
  });
}
